import { supabase } from './supabaseClient';

type UserRole = 'contador' | 'emprendedor' | 'admin';

interface ClaudeResponse {
  content: Array<{ type: string; text: string }>;
  usage: { input_tokens: number; output_tokens: number };
  quota?: { used: number; max: number };
}

export interface AskAssistantResult {
  text: string;
  /** null si el feature no tiene enforcement de cuota (ver QUOTA_ENFORCED_FEATURES en api/chat.ts). */
  quota: { used: number; max: number } | null;
}

/** Distingue "alcanzaste tu límite mensual" de cualquier otro error (red, Anthropic, etc.). */
export class QuotaExceededError extends Error {
  used?: number;
  max?: number;
  constructor(message: string, used?: number, max?: number) {
    super(message);
    this.name = 'QuotaExceededError';
    this.used = used;
    this.max = max;
  }
}

// Las instrucciones del sistema (identidad, alcance, guías de navegación y de pantallas) viven SOLO en el
// servidor: src/utils/aiChatPolicy.ts, armadas por api/chat.ts. El cliente solo envía la pregunta y el rol
// (hallazgo M-1: /api/chat ya no acepta `system`, `tools` ni `tool_choice` del cliente).

/**
 * Cuota mensual y modelo (Haiku/Sonnet) se resuelven 100% server-side en
 * /api/chat, a partir del firmId del token verificado — nunca del cliente.
 * Fix 2026-08-15 (gating de agentes IA): antes había un check client-side
 * (checkLimit/getDailyUsage sobre `ai_usage`, ventana diaria) que era la
 * única fuente de verdad — un cliente que se saltara este archivo (o que
 * llamara /api/chat directo) no tenía ningún límite real. Ese código se
 * eliminó junto con el increment post-respuesta que hacía este mismo
 * archivo — si hubiera quedado, cada mensaje se habría contado 2 veces
 * (una vez acá, otra en el RPC check_and_increment_ai_usage del servidor).
 * El único indicio de límite alcanzado hoy es el 429 que devuelve el
 * servidor — sin pre-check ni contador de uso en el cliente.
 */
export async function askAssistant(
  question: string,
  userRole: UserRole,
  lang: 'es' | 'en' = 'es',
): Promise<AskAssistantResult> {
  // Obtain session token — required by /api/chat auth guard
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Tu sesión expiró. Por favor recarga la página.');
  }

  const res = await fetch('/api/chat', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      question,
      role: userRole,
      lang,
      feature: 'chat',
    }),
  });

  if (!res.ok) {
    let errMsg = 'Error al conectar con el asistente.';
    let errType: string | undefined;
    let used: number | undefined;
    let max: number | undefined;
    try {
      const errBody = await res.json();
      if (errBody?.error?.message) errMsg = errBody.error.message;
      errType = errBody?.error?.type;
      used = errBody?.used;
      max = errBody?.max;
    } catch {
      // ignore parse errors
    }
    if (res.status === 429 && errType === 'quota_exceeded') {
      throw new QuotaExceededError(errMsg, used, max);
    }
    throw new Error(errMsg);
  }

  const data = await res.json() as ClaudeResponse & { type?: string; error?: { type?: string; message?: string } };

  // Detectar error de Anthropic (HTTP 200 con body de error)
  if (data.type === 'error' || !data.content) {
    const errType = data.error?.type ?? 'unknown';
    const errMsg  = data.error?.message ?? JSON.stringify(data);
    throw new Error(`Anthropic error: ${errType} — ${errMsg}`);
  }

  return { text: data.content[0]?.text ?? '', quota: data.quota ?? null };
}
